import type { StripeInterval } from "../subscriptions";

export type BillingPeriodCalendar = {
  periodEnd: number;
  billingCycleAnchor?: number;
  interval: StripeInterval;
  intervalCount: number;
};

const DAY_SECONDS = 86_400;

export function addUtcMonths(
  from: Date,
  months: number,
  dayOfMonth: number
): Date {
  const target = new Date(from);
  target.setUTCDate(1);
  target.setUTCMonth(target.getUTCMonth() + months);
  const lastDayOfMonth = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  target.setUTCDate(Math.min(dayOfMonth, lastDayOfMonth));
  return target;
}

// Stripe returns every period end to the billing-cycle anchor's day of month,
// clamped to the last day of a shorter month. Without the anchor, the current
// period end's day stands in for it only when that end was not clamped: an end
// on the last day of a month shorter than 31 days may belong to an anchor on a
// later day, and stepping from it would land up to three days early.
function anchorDayOfMonth(period: BillingPeriodCalendar): number | undefined {
  if (period.billingCycleAnchor != null) {
    return new Date(period.billingCycleAnchor * 1000).getUTCDate();
  }
  const periodEnd = new Date(period.periodEnd * 1000);
  const lastDayOfMonth = new Date(
    Date.UTC(periodEnd.getUTCFullYear(), periodEnd.getUTCMonth() + 1, 0)
  ).getUTCDate();
  const mayBeClamped =
    periodEnd.getUTCDate() === lastDayOfMonth && lastDayOfMonth < 31;
  return mayBeClamped ? undefined : periodEnd.getUTCDate();
}

function addBillingPeriods(
  period: BillingPeriodCalendar,
  periods: number
): number | undefined {
  switch (period.interval) {
    case "day":
      return period.periodEnd + periods * DAY_SECONDS;
    case "week":
      return period.periodEnd + periods * 7 * DAY_SECONDS;
    case "month":
    case "year": {
      const dayOfMonth = anchorDayOfMonth(period);
      if (dayOfMonth === undefined) {
        return undefined;
      }
      const months = (period.interval === "year" ? 12 : 1) * periods;
      return (
        addUtcMonths(
          new Date(period.periodEnd * 1000),
          months,
          dayOfMonth
        ).getTime() / 1000
      );
    }
  }
}

/**
 * The first period end at or after `atSec`, the one billing boundary a
 * period-end change can land on. An absent moment, or one at or before the
 * current period end, is the current period end. Undefined when a later period
 * end cannot be placed on Stripe's calendar: a guessed boundary would put a
 * Stripe cancel_at inside a period, which Stripe prorates.
 */
export function firstPeriodEndAtOrAfter(
  period: BillingPeriodCalendar,
  atSec: number | null | undefined
): number | undefined {
  let boundary: number | undefined = period.periodEnd;
  for (
    let periods = period.intervalCount;
    boundary !== undefined && atSec != null && boundary < atSec;
    periods += period.intervalCount
  ) {
    boundary = addBillingPeriods(period, periods);
  }
  return boundary;
}
